import React, {useCallback, useState, MouseEvent, useMemo} from 'react';
import {Node, useViewport, XYPosition} from 'reactflow';

import {assert} from 'Asserts';
import {ProcedureFlowchart} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';

import {reorderMethodsInLinearFlowchart} from '../flowchartManagementUtils';
import {COMMON_NODE_WIDTH, METHOD_BEING_DRAGGED_NODE_HEIGHT} from '../procedureMethodNodeCommonConsts';
import {ModifyFlowchartCallback} from '../useProcedureActions';
import {
  buildLinearFlowchartMethodDropZones,
  detectDraggingNodeDropZoneIntersection,
} from './dragOverlayManagementUtils';
import ProcedureDropZoneRenderer from './ProcedureDropZoneRenderer';
import ProcedureMethodBeingDraggedNodeRenderer from './ProcedureMethodBeingDraggedNodeRenderer';
import {DropZoneType, MethodBeingDraggedNode, MethodDropZone} from './ProcedureMethodDragOverlayTypes';

interface UseMethodDragOverlayOutputs {
  dragOverlayComponent: JSX.Element;
  methodCidBeingDragged: string | null;
  handleNodeDragStop: (event: MouseEvent, node: Node) => void;
  handleNodeDrag: (event: MouseEvent, node: Node) => void;
}

function MethodDragOverlay(props: {
  methodBeingDragged: MethodBeingDraggedNode | null;
  intersectingDropZone: MethodDropZone | null;
  viewportZoom: number;
}): JSX.Element {
  return props.methodBeingDragged != null ? (
    <div>
      {props.intersectingDropZone != null && (
        <ProcedureDropZoneRenderer dropZone={props.intersectingDropZone} zoom={props.viewportZoom} />
      )}
      <ProcedureMethodBeingDraggedNodeRenderer
        name={props.methodBeingDragged.name}
        position={props.methodBeingDragged.position}
        zoom={props.viewportZoom}
        isIntersectingDropZone={props.intersectingDropZone != null}
      />
    </div>
  ) : (
    <></>
  );
}

export function useMethodDragOverlay(
  flowchart: ProcedureFlowchart,
  modifyFlowchart: (cb: ModifyFlowchartCallback) => void,
  reactFlowWrapper: React.RefObject<HTMLDivElement>,
  canvasPointToClientPoint: (pos: XYPosition) => XYPosition,
  nodeIdToComputedCanvasPos: {[nodeId: string]: XYPosition},
  maxHeightByRow: {[row: number]: number},
  viewportZoom: number
): UseMethodDragOverlayOutputs {
  const [methodBeingDragged, setMethodBeingDragged] = useState<MethodBeingDraggedNode | null>(null);
  const [intersectingDropZone, setIntersectingDropZone] = useState<MethodDropZone | null>(null);
  const {x: viewportX, y: viewportY} = useViewport();
  const {procedureType} = ProcedurePageContext.useContext();

  /**
   * Include viewport as a dependency so that panning also re-triggers dropZone calculations
   */
  const dropZones = useMemo(
    () =>
      buildLinearFlowchartMethodDropZones(
        flowchart,
        nodeIdToComputedCanvasPos,
        canvasPointToClientPoint,
        maxHeightByRow
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canvasPointToClientPoint, flowchart, maxHeightByRow, nodeIdToComputedCanvasPos, viewportX, viewportY]
  );

  const handleNodeDragStop = useCallback(
    (event: MouseEvent, node: Node): void => {
      if (intersectingDropZone != null) {
        modifyFlowchart((fc) =>
          reorderMethodsInLinearFlowchart(procedureType, fc, node.id, intersectingDropZone)
        );
      }
      setMethodBeingDragged(null);
      setIntersectingDropZone(null);
    },
    [intersectingDropZone, procedureType, modifyFlowchart]
  );

  const handleNodeDrag = useCallback(
    (event: MouseEvent, node: Node): void => {
      if (!reactFlowWrapper.current) {
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const mousePosition = {
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      };

      const draggedMethodTopLeftPos = {
        x: mousePosition.x - (COMMON_NODE_WIDTH / 2) * viewportZoom,
        y: mousePosition.y - (METHOD_BEING_DRAGGED_NODE_HEIGHT / 2) * viewportZoom,
      };
      const maybeIntersectingDropZone = detectDraggingNodeDropZoneIntersection(
        draggedMethodTopLeftPos,
        dropZones,
        mousePosition,
        viewportZoom
      );

      if (maybeIntersectingDropZone != null) {
        let isValidDropZone = false;
        switch (maybeIntersectingDropZone.type) {
          case DropZoneType.METHOD_ROOT:
            isValidDropZone = maybeIntersectingDropZone.prependToMethodCid !== node.id;
            break;
          case DropZoneType.METHOD_EDGE:
            isValidDropZone =
              maybeIntersectingDropZone.fromMethod !== node.id &&
              maybeIntersectingDropZone.toMethod !== node.id;
            break;
          case DropZoneType.METHOD_LEAF:
            isValidDropZone = maybeIntersectingDropZone.appendFromMethodCid !== node.id;
            break;
          default:
            assert(false, 'Unsupported drop zone type');
        }
        setIntersectingDropZone(isValidDropZone ? maybeIntersectingDropZone : null);
      } else {
        setIntersectingDropZone(maybeIntersectingDropZone);
      }

      const methodBeingDraggedNode: MethodBeingDraggedNode = {
        id: node.id,
        name: node.data.name,
        position: mousePosition,
      };
      setMethodBeingDragged(methodBeingDraggedNode);
    },
    [dropZones, reactFlowWrapper, viewportZoom]
  );

  const dragOverlayComponent = (
    <MethodDragOverlay
      methodBeingDragged={methodBeingDragged}
      intersectingDropZone={intersectingDropZone}
      viewportZoom={viewportZoom}
    />
  );

  return {
    dragOverlayComponent,
    methodCidBeingDragged: methodBeingDragged?.id ?? null,
    handleNodeDrag,
    handleNodeDragStop,
  };
}
